import { Link } from "react-router-dom";
import { ArrowRight, Expand } from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import { useQuote } from "@/lib/quoteContext";

const IMAGES = [
  {
    src: "https://images.unsplash.com/photo-1769144256227-5185141c3aca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxjYXJnbyUyMHNoaXBwaW5nJTIwcG9ydCUyMGNvbnRhaW5lciUyMGFlcmlhbHxlbnwwfHx8fDE3NzgwMzkxMDJ8MA&ixlib=rb-4.1.0&q=85",
    alt: "Aerial view of stacked shipping containers",
    label: "Port Operations",
    span: "md:col-span-8 md:row-span-2",
  },
  {
    src: "https://images.unsplash.com/photo-1769144256207-bc4bb75b29db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMHNoaXBwaW5nJTIwcG9ydCUyMGNvbnRhaW5lciUyMGFlcmlhbHxlbnwwfHx8fDE3NzgwMzkxMDJ8MA&ixlib=rb-4.1.0&q=85",
    alt: "Cargo ship with colourful containers",
    label: "At Sea",
    span: "md:col-span-4",
  },
  {
    src: "https://images.unsplash.com/photo-1642418934307-cc2c5bf83e65?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMGNvbnRhaW5lciUyMGFic3RyYWN0fGVufDB8fHx8MTc3ODAzOTEyOHww&ixlib=rb-4.1.0&q=85",
    alt: "Shipping train car detail",
    label: "Rail Freight",
    span: "md:col-span-4",
  },
  {
    src: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1400&q=85",
    alt: "Modern architectural interior",
    label: "Interior Design",
    span: "md:col-span-6",
  },
  {
    src: "https://images.unsplash.com/photo-1766678340672-51e6be3a64d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMGNvbnRhaW5lciUyMGFic3RyYWN0fGVufDB8fHx8MTc3ODAzOTEyOHww&ixlib=rb-4.1.0&q=85",
    alt: "Cargo ship at night",
    label: "Night Routes",
    span: "md:col-span-6",
  },
  {
    src: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=1400&q=80",
    alt: "Yiwu wholesale market aisles",
    label: "Yiwu Market",
    span: "md:col-span-6",
  },
  {
    src: "https://images.unsplash.com/photo-1581091215367-9b6c00b3039e?auto=format&fit=crop&w=1400&q=80",
    alt: "Quality inspection on the factory floor",
    label: "Quality Inspection",
    span: "md:col-span-3",
  },
  {
    src: "https://images.unsplash.com/photo-1530138979926-58e0e4b88b66?auto=format&fit=crop&w=1400&q=80",
    alt: "Warehouse logistics operations",
    label: "Warehousing",
    span: "md:col-span-3",
  },
];

export default function GalleryPage() {
  const { openQuote } = useQuote();
  return (
    <main data-testid="gallery-page" className="bg-ink-50">
      <Navigation />

      <section
        data-testid="gallery-hero"
        className="relative pt-36 md:pt-44 pb-12 md:pb-16 bg-white border-b border-ink-100"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-5">
            Gallery
          </p>
          <h1 className="font-heading font-black text-ink-900 tracking-[-0.035em] leading-[0.92] text-5xl md:text-6xl lg:text-7xl max-w-4xl">
            Trade moves.<br />
            <span className="text-stroke">We document it.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-ink-500 text-base md:text-lg leading-relaxed">
            Field captures from Yiwu wholesale floors, factory inspections, port
            operations and trade-exhibition partnerships across our 15+ country
            network.
          </p>
        </div>
      </section>

      <section className="bg-ink-50 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5 auto-rows-[260px]">
            {IMAGES.map((img, i) => (
              <figure
                key={i}
                data-testid={`gallery-page-item-${i}`}
                className={`group relative overflow-hidden bg-ink ${img.span}`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.06]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/75 via-ink/10 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
                <figcaption className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white">
                  <span className="font-mono text-[11px] uppercase tracking-[0.22em]">
                    {img.label}
                  </span>
                  <Expand className="w-4 h-4 opacity-60 group-hover:opacity-100 transition-opacity" strokeWidth={1.6} />
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-14 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-5">
              Want a tour?
            </p>
            <h2 className="font-heading font-black text-ink-900 tracking-[-0.03em] leading-[0.95] text-4xl md:text-5xl">
              Visit the Yiwu market with us.
            </h2>
            <p className="mt-4 max-w-2xl text-ink-500 text-base md:text-lg leading-relaxed">
              We arrange visa letters, hotel and a multilingual team to walk you
              through wholesale floors, factories and supplier meetings.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end gap-3 flex-wrap">
            <button
              type="button"
              onClick={openQuote}
              data-testid="gallery-cta-quote"
              className="inline-flex items-center gap-2 bg-signal hover:bg-signal-600 text-white px-6 py-3.5 font-mono text-[12px] uppercase tracking-[0.22em] transition-colors"
            >
              Get a Quote
              <ArrowRight className="w-4 h-4" />
            </button>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 border border-ink-900 text-ink-900 hover:bg-ink-900 hover:text-white px-6 py-3.5 font-mono text-[12px] uppercase tracking-[0.22em] transition-colors"
            >
              Contact us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
